import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/Toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { TrendingUp, ShoppingCart, CreditCard, Tag, FileText, Calendar, RefreshCw } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export const ReportsPage = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  const [salesByPeriod, setSalesByPeriod] = useState({ data: [], summary: {} });
  const [salesByProduct, setSalesByProduct] = useState({ data: [] });
  const [salesByPayment, setSalesByPayment] = useState({ data: [] });
  const [salesByCategory, setSalesByCategory] = useState({ data: [] });
  const [fiscalSummary, setFiscalSummary] = useState({ resumo: {} });

  useEffect(() => {
    loadAllReports();
  }, []);

  const loadAllReports = async () => {
    setLoading(true);
    try {
      const params = `start_date=${startDate}T00:00:00Z&end_date=${endDate}T23:59:59Z`;
      
      const [periodRes, productRes, paymentRes, categoryRes, fiscalRes] = await Promise.all([
        api.get(`/reports/sales-by-period?period=${period}&${params}`),
        api.get(`/reports/sales-by-product?${params}&limit=10`),
        api.get(`/reports/sales-by-payment?${params}`),
        api.get(`/reports/sales-by-category?${params}`),
        api.get(`/reports/fiscal-summary?${params}`)
      ]);
      
      setSalesByPeriod(periodRes.data);
      setSalesByProduct(productRes.data);
      setSalesByPayment(paymentRes.data);
      setSalesByCategory(categoryRes.data);
      setFiscalSummary(fiscalRes.data);
      
      toast.success('Relatórios atualizados!');
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.name.includes('Vendas') 
                ? formatCurrency(entry.value) 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Relatórios</h1>
          <p className="text-slate-600">Análise completa de vendas e impostos</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label>Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label>Período</Label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-40 h-10 px-3 border border-slate-300 rounded-md bg-white"
              >
                <option value="daily">Diário</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
              </select>
            </div>
            <Button onClick={loadAllReports} disabled={loading} className="bg-slate-900 hover:bg-slate-800">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Vendas</p>
                <p className="text-2xl font-bold">{formatCurrency(salesByPeriod.summary?.total_vendas || 0)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Qtd. Vendas</p>
                <p className="text-2xl font-bold">{salesByPeriod.summary?.quantidade_vendas || 0}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Ticket Médio</p>
                <p className="text-2xl font-bold">{formatCurrency(salesByPeriod.summary?.ticket_medio || 0)}</p>
              </div>
              <CreditCard className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Impostos</p>
                <p className="text-2xl font-bold">{formatCurrency(fiscalSummary.resumo?.total_impostos || 0)}</p>
              </div>
              <FileText className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="period" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="period">
            <Calendar className="w-4 h-4 mr-2" />
            Por Período
          </TabsTrigger>
          <TabsTrigger value="products">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Por Produto
          </TabsTrigger>
          <TabsTrigger value="payment">
            <CreditCard className="w-4 h-4 mr-2" />
            Por Pagamento
          </TabsTrigger>
          <TabsTrigger value="category">
            <Tag className="w-4 h-4 mr-2" />
            Por Categoria
          </TabsTrigger>
          <TabsTrigger value="fiscal">
            <FileText className="w-4 h-4 mr-2" />
            Fiscal
          </TabsTrigger>
        </TabsList>

        {/* Vendas por Período */}
        <TabsContent value="period">
          <Card>
            <CardHeader>
              <CardTitle>Vendas por Período</CardTitle>
              <CardDescription>Evolução das vendas no período selecionado</CardDescription>
            </CardHeader>
            <CardContent>
              {salesByPeriod.data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={salesByPeriod.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo" />
                    <YAxis yAxisId="left" tickFormatter={(v) => `R$${v}`} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="total_vendas" name="Total Vendas" stroke="#0088FE" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="quantidade_vendas" name="Qtd. Vendas" stroke="#00C49F" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Nenhuma venda encontrada no período
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas por Produto */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Produtos Mais Vendidos</CardTitle>
              <CardDescription>Produtos com maior quantidade vendida</CardDescription>
            </CardHeader>
            <CardContent>
              {salesByProduct.data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={salesByProduct.data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="produto" type="category" width={150} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="quantidade_vendida" name="Quantidade" fill="#0088FE" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Nenhuma venda encontrada no período
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas por Forma de Pagamento */}
        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle>Vendas por Forma de Pagamento</CardTitle>
              <CardDescription>Distribuição por método de pagamento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  {salesByPayment.data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={salesByPayment.data}
                          dataKey="total"
                          nameKey="forma_pagamento"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ forma_pagamento, percent }) => `${forma_pagamento} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {salesByPayment.data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-500">
                      Nenhuma venda encontrada
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold">Detalhamento</h3>
                  {salesByPayment.data?.map((item, index) => (
                    <div key={item.forma_pagamento} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span>{item.forma_pagamento}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(item.total)}</p>
                        <p className="text-xs text-slate-500">{item.quantidade} vendas</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas por Categoria */}
        <TabsContent value="category">
          <Card>
            <CardHeader>
              <CardTitle>Vendas por Categoria</CardTitle>
              <CardDescription>Desempenho por categoria de produto</CardDescription>
            </CardHeader>
            <CardContent>
              {salesByCategory.data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={salesByCategory.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="categoria" />
                    <YAxis yAxisId="left" tickFormatter={(v) => `R$${v}`} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="total_vendas" name="Total Vendas (R$)" fill="#0088FE" />
                    <Bar yAxisId="right" dataKey="quantidade_vendida" name="Quantidade" fill="#00C49F" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Nenhuma venda encontrada no período
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resumo Fiscal */}
        <TabsContent value="fiscal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Resumo de Impostos</CardTitle>
                <CardDescription>Total de impostos no período</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Base de Cálculo</span>
                    <span className="font-bold text-lg">{formatCurrency(fiscalSummary.resumo?.base_calculo_total || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <div>
                      <span className="font-medium">ICMS</span>
                      <p className="text-xs text-slate-500">Imposto sobre Circulação</p>
                    </div>
                    <span className="font-bold text-lg text-blue-700">{formatCurrency(fiscalSummary.resumo?.total_icms || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-lg">
                    <div>
                      <span className="font-medium">IBS</span>
                      <p className="text-xs text-slate-500">Imposto sobre Bens e Serviços (Estadual/Municipal)</p>
                    </div>
                    <span className="font-bold text-lg text-emerald-700">{formatCurrency(fiscalSummary.resumo?.total_ibs || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                    <div>
                      <span className="font-medium">CBS</span>
                      <p className="text-xs text-slate-500">Contribuição sobre Bens e Serviços (Federal)</p>
                    </div>
                    <span className="font-bold text-lg text-purple-700">{formatCurrency(fiscalSummary.resumo?.total_cbs || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-amber-100 rounded-lg border-2 border-amber-300">
                    <span className="font-bold">TOTAL DE IMPOSTOS</span>
                    <span className="font-bold text-2xl text-amber-700">{formatCurrency(fiscalSummary.resumo?.total_impostos || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Distribuição de Impostos</CardTitle>
                <CardDescription>Proporção de cada imposto</CardDescription>
              </CardHeader>
              <CardContent>
                {fiscalSummary.resumo?.total_impostos > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'ICMS', value: fiscalSummary.resumo?.total_icms || 0 },
                          { name: 'IBS', value: fiscalSummary.resumo?.total_ibs || 0 },
                          { name: 'CBS', value: fiscalSummary.resumo?.total_cbs || 0 }
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                      >
                        <Cell fill="#3B82F6" />
                        <Cell fill="#10B981" />
                        <Cell fill="#8B5CF6" />
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-500">
                    Nenhum imposto calculado no período
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
